import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import {
  Card,
  Icon,
  DataTable,
  TextStyle,
  Pagination,
  SkeletonDisplayText
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { DIRECTION, NUMBER_SYNONYMS_PER_PAGE, SynonymType } from 'src/constants/synonym';
import { prettyText } from 'src/helpers';
import ActionsGroupOnListing from 'src/components/views/synonym/actions-group-on-listing';
import NoSynonym, { NotFoundSynonym } from 'src/components/views/synonym/no-synonym';
import TableHeadingCellWithTooltip from 'src/components/views/synonym/table-heading-cell-with-tooltip';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import * as synonymActions from 'src/stores/actions/synonym-actions';

import { EditMajor, DeleteMajor, MobileAcceptMajor, MinusMinor } from '@shopify/polaris-icons';
import EditSynonymModal from './edit-synonym-modal';
import DeleteSynonymModal from './delete-synonym-modal';
import { SynonymFilter } from './synonym-filter';
import DirectionTooltip from './direction-tooltip';
import SuggestionTooltip from './suggestion-tooltip';

const renderRowsLoadingFilter = count => {
  let rows = [];
  for (var i = 0; i < count; i++) {
    rows.push([
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />
    ]);
  }

  return rows;
};

const ManualSynonymSetting = props => {
  const { i18n, loading, synonyms, total, setSynonymFilter } = props;

  const [filter, setFilter] = React.useState({ search: '', sort: '', status: '' });
  const [paging, setPaging] = React.useState({});
  const [openEdit, setOpenEdit] = React.useState(false);
  const [deleteIds, setDeleteIds] = React.useState([]);
  const [synonymEdit, setSynonymEdit] = React.useState({});
  const [hasNext, setHasNext] = React.useState(true);
  const [hasPrevious, setHasPrevious] = React.useState(false);
  const { skip = 0, limit = NUMBER_SYNONYMS_PER_PAGE } = paging;

  React.useEffect(() => {
    const hasNext = skip + limit < total;
    const hasPrevious = skip > 0;
    setHasNext(hasNext);
    setHasPrevious(hasPrevious);
  }, [skip, limit, total]);

  const buildRows = () => {
    let rows = [];
    if (!_.isEmpty(synonyms)) {
      _.forEach(synonyms, synonym => {
        const { id, searchTerm, synonyms, biDirection } = synonym;
        const directionValue = _.find(DIRECTION, item => item.value === biDirection);
        const directionLabel = directionValue ? directionValue.label : '';

        const synonymsList = _.isArray(synonyms) ? synonyms : synonyms.split(',');
        if (id) {
          rows.push([
            renderCheckbox(synonym),
            <span>{searchTerm} </span>,
            <TextStyle variation="strong">{directionLabel} </TextStyle>,
            <span className="synonyms">
              {synonymsList.map((synonym, index) => (
                <span className="synonym-item" key={index}>
                  {synonym ? synonym.trim() : ''}
                </span>
              ))}
            </span>,
            <span className="actionIcon">
              <Icon source={synonym.allowSuggestion ? MobileAcceptMajor : MinusMinor} />
            </span>,
            <div className="flex-align-center">
              <div
                style={{ marginRight: '0.6rem' }}
                onClick={() => {
                  setSynonymEdit(synonym);
                  setOpenEdit(true);
                }}>
                <span className="actionIcon">
                  <Icon source={EditMajor} />
                </span>
              </div>
              <div
                onClick={() => {
                  setDeleteIds([id]);
                }}>
                <span className="actionIcon">
                  <Icon source={DeleteMajor} />
                </span>
              </div>
            </div>
          ]);
        } else {
          rows.push([
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />
          ]);
        }
      });
    }

    return rows;
  };
  const renderCheckbox = synonym => {
    return (
      <label className="BC-Checkbox-Status">
        <input
          type="checkbox"
          checked={synonym.enabled}
          onChange={() => handleChangeStatus(synonym)}
        />
        <span />
      </label>
    );
  };
  const handleChangeStatus = synonym => {
    const { updateSynonyms } = props;

    const newStatus = !synonym.enabled;
    const newSynonym = {
      ...synonym,
      synonyms: _.map(synonym.synonyms.split(','), text => prettyText(text)),
      biDirection: synonym.biDirection === '<=>',
      enabled: newStatus
    };
    updateSynonyms([newSynonym], 'changeStatus');
  };

  const handleSearch = () => {
    let searchifyText = prettyText(filter.search.toLowerCase().trim());
    setPaging({ ...paging, skip: 0, limit: NUMBER_SYNONYMS_PER_PAGE });
    props.getSynonyms(searchifyText || '', 0, NUMBER_SYNONYMS_PER_PAGE, filter.sort, filter.status);
  };

  // Go to previous page
  const getPreviousPage = () => {
    const previousSkip = skip - limit;
    const previousLimit = NUMBER_SYNONYMS_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    setPaging({ ...paging, skip: previousSkip, limit: previousLimit });

    props.getSynonyms(filter.search || '', previousSkip, previousLimit, filter.sort, filter.status);
  };

  // Go to next page
  const getNextPage = () => {
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    setPaging({ ...paging, skip: nextSkip, limit });

    props.getSynonyms(
      filter.search || '',
      nextSkip,
      NUMBER_SYNONYMS_PER_PAGE,
      filter.sort,
      filter.status
    );
  };

  React.useEffect(() => {
    handleSearch();
    // eslint-disable-next-line
  }, [filter]);

  // Render synonyms on table
  let rowSynonyms;
  if (!loading) {
    rowSynonyms = buildRows();
  } else {
    const numberRender = _.isEmpty(synonyms) ? 5 : synonyms.length;
    rowSynonyms = renderRowsLoadingFilter(numberRender);
  }

  // Check having synonym
  const havingSynonyms = !_.isEmpty(synonyms);

  return (
    <>
      <Card sectioned>
        <div className="SynonymTable__Header">
          <Card.Header>
            <SynonymFilter
              filter={filter}
              setFilter={filter => {
                setFilter(filter);
                setSynonymFilter(filter);
              }}
              isManual
              i18n={i18n}
            />
          </Card.Header>
        </div>
        <div className={`listSynonyms ${!havingSynonyms && !loading ? 'empty' : ''}`}>
          <DataTable
            verticalAlign="baseline"
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
            headings={[
              i18n.translate('AdminHTML.Synonym.Synonym.status'),
              i18n.translate('AdminHTML.Synonym.Synonym.searchTerm'),
              <TableHeadingCellWithTooltip
                heading={i18n.translate('AdminHTML.Synonym.Synonym.direction')}
                tooltipNode={<DirectionTooltip i18n={i18n} />}
              />,
              <TableHeadingCellWithTooltip
                heading={i18n.translate('AdminHTML.Synonym.Synonym.synonyms')}
                tooltipContent={i18n.translate('AdminHTML.Synonym.Synonym.synonyms_Tooltip')}
              />,
              <TableHeadingCellWithTooltip
                heading={i18n.translate('AdminHTML.Synonym.Synonym.suggestionList')}
                tooltipNode={<SuggestionTooltip i18n={i18n} />}
              />,
              <div className="align-center">
                {i18n.translate('AdminHTML.Synonym.Synonym.actions')}
              </div>
            ]}
            rows={rowSynonyms}
          />
        </div>
        {!havingSynonyms &&
          !loading &&
          (!filter.search && !filter.status ? (
            <NoSynonym i18n={i18n} />
          ) : (
            <NotFoundSynonym i18n={i18n} />
          ))}
      </Card>
      <EditSynonymModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        reload={() => handleSearch()}
        synonymInput={synonymEdit}
      />
      <DeleteSynonymModal
        synonymType={SynonymType.MANUAL}
        open={!_.isEmpty(deleteIds)}
        onClose={() => setDeleteIds([])}
        reload={() => handleSearch()}
        synonymIds={deleteIds}
      />

      {havingSynonyms ? (
        <div className="pagination">
          {(hasPrevious || hasNext) && (
            <Pagination
              hasPrevious={hasPrevious}
              onPrevious={getPreviousPage}
              hasNext={hasNext}
              onNext={getNextPage}
            />
          )}

          <div className="actionsGroup">
            <ActionsGroupOnListing skip={skip} limit={limit} />
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSynonyms: (q, skip, limit, sort, status) =>
    dispatch(synonymActions.getSynonyms({ q, skip, limit, sort, status }, ownProps.i18n)),
  updateSynonyms: (synonyms, actionName) =>
    dispatch(synonymActions.updateSynonyms(synonyms, actionName, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    synonyms: _.get(state.synonym, 'synonyms', [{}, {}, {}]),
    loading: _.get(state.synonym, 'loading', false),
    total: _.get(state.synonym, 'total', 0)
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(ManualSynonymSetting)),
  'ManualSynonymSetting'
);
